'use client';

import React, { useEffect, useRef, useState } from 'react';
import { StaticDynamicLink as Link } from '@/lib/static-export-nav';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { motion } from 'framer-motion';
import { useAuthStore } from '@/lib/auth-store';
import { getEffectiveRole, isAdminRole, isJuryRole, isOrganizerRole, normalizeRole } from '@/lib/rbac';
import {
  ADMIN_DEDICATED_ROUTES,
  ADMIN_NAV_SECTIONS,
  buildAdminNavItemHref,
  getAdminNavItemStatus,
  hasDedicatedAdminRoute,
  type AdminNavItem,
} from '@/config/admin-navigation';
import { AdminNavLink } from '@/components/admin/AdminNavLink';
import { adminNavHrefIsActive } from '@/lib/admin-routing';
import { isFeatureFlagVisible } from '@/config/admin-feature-flags';
import { hasPermission } from '@/lib/permissions';
import { canAccessAdminNavItem } from '@/lib/admin-access';
import { useOptionalAdminBadgesContext } from '@/contexts/AdminBadgesContext';
import {
  Search,
  ChevronDown,
  LogOut,
  LayoutDashboard,
  Calendar,
  CalendarDays,
  BarChart3,
  Users,
  User,
  Settings,
  Ticket,
  MessageCircle,
  FileText,
  UserPlus,
  Bell,
  Award,
  ListChecks,
  ClipboardList,
  ShieldCheck,
  Landmark,
  RotateCcw,
  Building2,
  Layers,
} from 'lucide-react';
import { SidebarBrandLogo } from '@/components/layout/SidebarBrandLogo';
import { startNavigationLoadingForHref } from '@/lib/navigation-loading';

type NavItem = {
  href: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
};

const participantNavSections: { title: string; items: NavItem[] }[] = [
  {
    title: 'Main',
    items: [{ href: '/dashboard', label: 'Overview', icon: LayoutDashboard }],
  },
  {
    title: 'MY ACTIVITY',
    items: [
      { href: '/dashboard/profile', label: 'Profile', icon: User },
      { href: '/dashboard/my-events', label: 'My Events', icon: Calendar },
      { href: '/dashboard/my-tickets', label: 'Tickets', icon: Ticket },
      { href: '/dashboard/my-submissions', label: 'Submissions', icon: FileText },
    ],
  },
  {
    title: 'Account',
    items: [
      { href: '/dashboard/certificates', label: 'Certificates', icon: Award },
      { href: '/dashboard/notifications', label: 'Notifications', icon: Bell },
      { href: '/dashboard/settings', label: 'Settings', icon: Settings },
    ],
  },
];

const participantNav: NavItem[] = participantNavSections.flatMap((section) => section.items);

const organizerNavSections: { title: string; items: NavItem[] }[] = [
  {
    title: 'Main',
    items: [{ href: '/dashboard/organizer', label: 'Overview', icon: LayoutDashboard }],
  },
  {
    title: 'Events',
    items: [
      { href: '/dashboard/events', label: 'My Events', icon: CalendarDays },
      { href: '/dashboard/conferences', label: 'Conferences', icon: Layers },
      { href: '/dashboard/forms', label: 'Forms', icon: ClipboardList },
      { href: '/dashboard/calendar', label: 'Calendar', icon: Calendar },
      { href: '/dashboard/analytics', label: 'Analytics', icon: BarChart3 },
      { href: '/dashboard/participants', label: 'Attendees', icon: Users },
    ],
  },
  {
    title: 'Finance',
    items: [
      { href: '/dashboard/organizer/finance', label: 'Finance', icon: Landmark },
      { href: '/dashboard/organizer/kyc', label: 'Identity & KYC', icon: ShieldCheck },
    ],
  },
  {
    title: 'Account',
    items: [
      { href: '/dashboard/notifications', label: 'Notifications', icon: Bell },
      { href: '/dashboard/organizer/org-chat', label: 'Organization Chat', icon: Building2 },
      { href: '/dashboard/support', label: 'Support Chat', icon: MessageCircle },
      { href: '/dashboard/profile', label: 'Profile', icon: User },
      { href: '/dashboard/settings/subscription', label: 'Settings', icon: Settings },
    ],
  },
];

const organizerNav: NavItem[] = organizerNavSections.flatMap((section) => section.items);

const juryNav: NavItem[] = [
  { href: '/jury', label: 'Overview', icon: LayoutDashboard },
  { href: '/jury/profile', label: 'Jury Profile', icon: User },
  { href: '/jury/assigned-events', label: 'Assigned Events', icon: CalendarDays },
  { href: '/jury/evaluations', label: 'My Evaluations', icon: ListChecks },
];

const SIDEBAR_BY_ROLE: Record<
  'admin' | 'organizer' | 'jury' | 'participant',
  { title: string; items: NavItem[] }
> = {
  admin: {
    title: 'Administrative Control',
    items: [],
  },
  organizer: {
    title: 'Organizer Environment',
    items: organizerNav,
  },
  jury: {
    title: 'Jury Environment',
    items: juryNav,
  },
  participant: {
    title: 'Network Activities',
    items: participantNav,
  },
};

function isOrganizerNavRole(role: string): boolean {
  const normalized = normalizeRole(role);
  return isOrganizerRole(role) || ['event_manager', 'team_manager'].includes(normalized);
}

function getSidebarConfigForRole(role: string) {
  if (isAdminRole(role)) return SIDEBAR_BY_ROLE.admin;
  if (isOrganizerNavRole(role)) return SIDEBAR_BY_ROLE.organizer;
  if (isJuryRole(role)) return SIDEBAR_BY_ROLE.jury;
  return SIDEBAR_BY_ROLE.participant;
}

function resolveActiveNavHref(
  pathname: string | null,
  items: NavItem[],
  exactOnlyHrefs: Set<string> = new Set(),
): string | null {
  if (!pathname) return null;
  const current = pathname.split('?')[0].replace(/\/$/, '') || '/';

  let best: string | null = null;
  let bestLen = -1;

  for (const item of items) {
    const target = item.href.split('?')[0].replace(/\/$/, '') || '/';
    const exact = current === target;
    const nested = !exactOnlyHrefs.has(target) && current.startsWith(`${target}/`);
    if (exact || nested) {
      if (target.length > bestLen) {
        best = item.href;
        bestLen = target.length;
      }
    }
  }

  return best;
}

const ORGANIZER_EXACT_ONLY = new Set(['/dashboard/organizer']);
const JURY_EXACT_ONLY = new Set(['/jury']);
const PARTICIPANT_EXACT_ONLY = new Set(['/dashboard']);

function exactOnlyForRole(role: string): Set<string> {
  if (isOrganizerNavRole(role)) return ORGANIZER_EXACT_ONLY;
  if (isJuryRole(role)) return JURY_EXACT_ONLY;
  return PARTICIPANT_EXACT_ONLY;
}

function OrganizerSidebarComponent() {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const { user, _hasHydrated, logout } = useAuthStore();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const effectiveRole = getEffectiveRole(user);
  const sidebarConfig = getSidebarConfigForRole(effectiveRole);
  const isAdminSidebar = isAdminRole(effectiveRole);
  const isOrganizerSidebar = !isAdminSidebar && isOrganizerNavRole(effectiveRole);
  const isJurySidebar = !isAdminSidebar && isJuryRole(effectiveRole);
  const isParticipantSidebar = !isAdminSidebar && !isOrganizerSidebar && !isJurySidebar;
  const [adminQuery, setAdminQuery] = React.useState('');
  const [collapsedSections, setCollapsedSections] = React.useState<Record<string, boolean>>({});
  /** Instant highlight while App Router is still loading the next page. */
  const [pendingHref, setPendingHref] = useState<string | null>(null);
  const adminBadgesContext = useOptionalAdminBadgesContext();
  const badges = adminBadgesContext?.badges ?? {};

  useEffect(() => {
    setPendingHref(null);
  }, [pathname, searchParams]);

  // Never leave the sidebar stuck on a pending highlight if navigation stalls.
  useEffect(() => {
    if (!pendingHref) return;
    const timer = window.setTimeout(() => setPendingHref(null), 2500);
    return () => window.clearTimeout(timer);
  }, [pendingHref]);

  useEffect(() => {
    if (_hasHydrated && user && isOrganizerSidebar && organizerNav.length === 0) {
      console.warn('[OrganizerSidebar] Organizer navigation items are empty for user:', {
        userId: user.id,
        role: effectiveRole,
      });
    }
  }, [_hasHydrated, user, isOrganizerSidebar, effectiveRole]);

  useEffect(() => {
    if (!isAdminSidebar) return;
    const defaults = ADMIN_NAV_SECTIONS.reduce<Record<string, boolean>>((acc, section) => {
      acc[section.id] = section.defaultExpanded === false;
      return acc;
    }, {});

    try {
      const saved = sessionStorage.getItem('admin-sidebar-collapsed');
      if (saved) {
        setCollapsedSections({ ...defaults, ...JSON.parse(saved) });
      }
      const savedQuery = sessionStorage.getItem('admin-sidebar-search');
      if (savedQuery) setAdminQuery(savedQuery);
      return;
    } catch {
      // Ignore malformed storage.
    }

    setCollapsedSections(defaults);
  }, [isAdminSidebar]);

  useEffect(() => {
    if (!isAdminSidebar) return;
    sessionStorage.setItem('admin-sidebar-collapsed', JSON.stringify(collapsedSections));
  }, [collapsedSections, isAdminSidebar]);

  useEffect(() => {
    if (!isAdminSidebar) return;
    sessionStorage.setItem('admin-sidebar-search', adminQuery);
  }, [adminQuery, isAdminSidebar]);

  useEffect(() => {
    const savedScroll = sessionStorage.getItem('sidebar-scroll-pos');
    if (savedScroll && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = parseInt(savedScroll, 10);
    }
  }, []);

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      sessionStorage.setItem('sidebar-scroll-pos', scrollContainerRef.current.scrollTop.toString());
    }
  };

  const normalizeHrefPath = (href: string) => href.split('?')[0].replace(/\/$/, '') || '/';

  const hrefQueryMatches = (href: string) => {
    const rawSearch = href.includes('?') ? href.slice(href.indexOf('?') + 1) : '';
    const hrefParams = new URLSearchParams(rawSearch);
    const hrefView = hrefParams.get('view');
    const currentView = searchParams?.get('view') ?? null;
    const hrefPath = normalizeHrefPath(href);

    // Sibling links on the same path must match their view= query exactly.
    if (hrefView != null) {
      return currentView === hrefView;
    }

    // Bare Legal Center link: only active when no view filter is selected.
    if (hrefPath === '/dashboard/admin/legal-documents') {
      return currentView == null;
    }

    if (hrefParams.has('tab')) {
      return (searchParams?.get('tab') ?? null) === hrefParams.get('tab');
    }

    return true;
  };

  const isActive = (href: string, navItems: NavItem[] = sidebarConfig.items) => {
    const hrefPath = normalizeHrefPath(href);

    if (pendingHref) {
      return normalizeHrefPath(pendingHref) === hrefPath;
    }

    if (isAdminSidebar) {
      const pathActive = adminNavHrefIsActive(pathname, hrefPath);

      if (!pathActive) {
        if (href.includes('?')) {
          const [basePath, search] = href.split('?');
          const params = new URLSearchParams(search);
          const tab = params.get('tab');
          const currentTab = searchParams?.get('tab');
          return pathname === basePath && currentTab === tab;
        }
        return false;
      }

      return hrefQueryMatches(href);
    }

    const activeHref = resolveActiveNavHref(pathname, navItems, exactOnlyForRole(effectiveRole));
    return activeHref === href;
  };

  const markPendingNavigation = (href: string) => {
    const nextPath = normalizeHrefPath(href);
    const currentPath = normalizeHrefPath(pathname || '/');
    if (nextPath !== currentPath) {
      startNavigationLoadingForHref(href, pathname);
      setPendingHref(href);
    }
  };

  const toggleAdminSection = (sectionId: string) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const normalizedQuery = adminQuery.trim().toLowerCase();

  const visibleAdminSections = React.useMemo(() => {
    const isInternalUser = user?.email?.endsWith('@enthioshub.com') ?? false;

    return ADMIN_NAV_SECTIONS
      .filter((section) => {
        if (section.hidden) return false;
        if (!isFeatureFlagVisible(section.featureFlag, { isInternalUser })) return false;
        return hasPermission(user, section.permission);
      })
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => {
          if (item.hidden && !normalizedQuery) return false;
          if (item.legacy) return false;
          if (!isFeatureFlagVisible(item.featureFlag, { isInternalUser })) return false;
          if (!canAccessAdminNavItem(user, item.tab, item.permission, item.superPlatformOnly)) return false;

          if (!normalizedQuery) return true;
          const haystack = [
            item.label,
            item.description,
            ...(item.aliases ?? []),
            ...(item.keywords ?? []),
            section.label,
          ]
            .join(' ')
            .toLowerCase();
          return haystack.includes(normalizedQuery);
        }),
      }))
      .filter((section) => section.items.length > 0);
  }, [normalizedQuery, user]);



  const renderBadge = (item: AdminNavItem) => {
    const value = item.badgeKey ? Number(badges[item.badgeKey] ?? 0) : 0;
    if (!Number.isFinite(value) || value <= 0) return null;
    return (
      <motion.span
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="ml-auto inline-flex min-w-[20px] h-5 items-center justify-center rounded-full bg-nature-accent text-text-inverse text-[10px] font-black px-1.5"
      >
        {value > 99 ? '99+' : value}
      </motion.span>
    );
  };

  const renderHighlightedLabel = (label: string) => {
    if (!normalizedQuery) return <span className="relative z-10">{label}</span>;
    const idx = label.toLowerCase().indexOf(normalizedQuery);
    if (idx < 0) return <span className="relative z-10">{label}</span>;
    const before = label.slice(0, idx);
    const match = label.slice(idx, idx + normalizedQuery.length);
    const after = label.slice(idx + normalizedQuery.length);
    return (
      <span className="relative z-10">
        {before}
        <mark className="bg-nature-accent/25 text-current rounded px-0.5">{match}</mark>
        {after}
      </span>
    );
  };

  const renderNav = (title: string, items: NavItem[], allItems: NavItem[] = items) => (
    <div className="space-y-1">
      <div className="px-3">
        <p className="eh-sidebar-section">
          {title}
        </p>
      </div>
      <nav className="sidebar-inner space-y-1 px-1">
        {items.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.href, allItems);
          return (
            <Link
              key={`${item.label}-${item.href}`}
              href={item.href}
              prefetch={false}
              onClick={() => markPendingNavigation(item.href)}
              className={`eh-sidebar-link group relative overflow-hidden ${
                active ? 'eh-sidebar-link--pill-active' : ''
              }`}
            >
              {active ? (
                <span className="absolute left-0 top-2 bottom-2 w-1 bg-primary rounded-r-full" />
              ) : null}
              <Icon className={`eh-sidebar-link-icon ${active ? '' : 'group-hover:text-text-inverse'}`} />
              <span className="relative z-10">{item.label}</span>
            </Link>
          );
        })}
      </nav>
    </div>
  );

  const renderParticipantNav = () => (
    <div className="space-y-5">
      {participantNavSections.map((section) => (
        <div key={section.title}>{renderNav(section.title, section.items, participantNav)}</div>
      ))}
    </div>
  );

  const renderOrganizerNav = () => (
    <div className="space-y-5">
      {organizerNavSections.map((section) => (
        <div key={section.title}>{renderNav(section.title, section.items, organizerNav)}</div>
      ))}
    </div>
  );

  const tabParam = searchParams?.get('tab');
  const isMessagesPage = Boolean(
    pathname?.includes('/admin/messages') ||
    pathname?.includes('/dashboard/admin/messages') ||
    (pathname?.includes('/admin') && (tabParam === 'message-inbox' || tabParam === 'messages')) ||
    (pathname?.includes('/dashboard/admin') && (tabParam === 'message-inbox' || tabParam === 'messages'))
  );

  const renderAdminNav = () => {
    const searchContainerClasses = isMessagesPage
      ? 'sticky top-0 z-10 px-0.5 pb-2 pt-1 bg-soot/95 backdrop-blur-md mb-1'
      : 'sticky top-0 z-10 px-1 pb-2 pt-1 bg-soot';

    const searchInputClasses = isMessagesPage
      ? 'w-full h-9 rounded-lg bg-white/[0.06] border border-white/10 text-text-inverse text-xs pl-9 pr-3 placeholder:text-white/40 outline-none focus:border-white/25 focus:bg-white/10 focus:ring-1 focus:ring-white/15 transition-all font-medium'
      : 'w-full h-10 rounded-xl bg-white/[0.08] border border-white/10 text-text-inverse text-sm pl-9 pr-3 placeholder:text-text-inverse/50 outline-none focus:border-white/30 focus:bg-white/10 transition-colors';

    const searchIconClasses = isMessagesPage
      ? 'w-3.5 h-3.5 text-white/40 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none'
      : 'w-4 h-4 text-text-inverse/70 absolute left-3 top-1/2 -translate-y-1/2';

    return (
      <div className="space-y-2 px-3 pt-2">
        <div className={searchContainerClasses}>
          <label className="relative block">
            <Search className={searchIconClasses} />
            <input
              value={adminQuery}
              onChange={(event) => setAdminQuery(event.target.value)}
              placeholder="Search admin modules..."
              className={searchInputClasses}
            />
          </label>
        </div>
        {visibleAdminSections.map((section) => {
        const collapsed = Boolean(collapsedSections[section.id]);
        return (
          <div key={section.id} className="rounded-2xl border border-white/5 bg-bg/[0.02]">
            <button
              type="button"
              onClick={() => toggleAdminSection(section.id)}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-bg/[0.03] rounded-2xl transition-colors"
            >
              <p className="eh-sidebar-section !px-0 !pt-0">
                {section.label}
              </p>
              <ChevronDown
                className={`w-4 h-4 text-text-inverse/75 transition-transform duration-300 ${collapsed ? '-rotate-90' : 'rotate-0'}`}
              />
            </button>

            <motion.div
              initial={false}
              animate={{
                height: collapsed ? 0 : 'auto',
                opacity: collapsed ? 0 : 1,
              }}
              transition={{ duration: 0.12, ease: 'easeOut' }}
              className="overflow-hidden"
            >
              <nav className="sidebar-inner space-y-1 px-2 pb-3">
                {section.items.map((item) => {
                  const href = buildAdminNavItemHref(item);
                  const Icon = item.icon;
                  const active = isActive(href);
                  const itemStatus = getAdminNavItemStatus(item);
                  return (
                    <AdminNavLink
                      key={item.id}
                      href={href}
                      label={item.label}
                      status={itemStatus}
                      isActive={active}
                      onClick={() => markPendingNavigation(href)}
                      className={`eh-sidebar-link group relative overflow-hidden font-bold ${
                        active
                          ? 'eh-sidebar-link--pill-active'
                          : ''
                      }`}
                      activeClassName="eh-sidebar-link--pill-active"
                      disabledClassName="eh-sidebar-link opacity-45 cursor-not-allowed select-none hover:bg-white/[0.03]"
                    >
                      {active ? (
                        <span className="absolute left-0 top-2 bottom-2 w-1 bg-primary rounded-r-full" />
                      ) : null}
                      <Icon className={`eh-sidebar-link-icon ${active ? '' : 'group-hover:text-text-inverse'}`} />
                      {renderHighlightedLabel(item.label)}
                      {renderBadge(item)}
                    </AdminNavLink>
                  );
                })}
              </nav>
            </motion.div>
          </div>
        );
      })}
      {visibleAdminSections.length === 0 && (
        <div className="px-3 py-8 text-center eh-sidebar-muted text-xs font-semibold tracking-wide">
          No admin modules match your search.
        </div>
      )}
    </div>
    );
  };

  return (
    <aside className="eh-sidebar flex md:sticky md:top-0 h-screen sidebar shrink-0 w-full overflow-hidden">
      {/* Brand Header */}
      <Link href="/" className="block px-5 pt-6 pb-4" aria-label="EnthiosHub home">
        <SidebarBrandLogo />
      </Link>

      {/* Navigation Scroll Area */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className={`eh-sidebar-scroll custom-scrollbar space-y-2 pt-2 ${isParticipantSidebar ? 'pb-4' : 'space-y-6 pb-20'}`}
      >
        {!_hasHydrated ? (
          <div className="px-6 py-10 space-y-4">
            <div className="h-4 bg-bg/5 rounded-full w-1/2 animate-pulse" />
            <div className="space-y-2">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-10 bg-bg/5 rounded-2xl w-full animate-pulse" />
              ))}
            </div>
          </div>
        ) : isAdminSidebar ? (
          renderAdminNav()
        ) : isOrganizerSidebar ? (
          renderOrganizerNav()
        ) : isParticipantSidebar ? (
          renderParticipantNav()
        ) : (
          renderNav(sidebarConfig.title, sidebarConfig.items)
        )}
      </div>
      {isParticipantSidebar && user ? (
        <div className="shrink-0 border-t border-white/10 px-3 py-3">
          <div className="flex items-center gap-3 rounded-2xl px-3 py-2">
            <div className="h-9 w-9 rounded-full bg-bg/10 flex items-center justify-center text-text-inverse/80 text-xs font-bold uppercase">
              {(user.name || user.email || 'U').slice(0, 1)}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-text-inverse truncate">{user.name || 'Account'}</p>
              <p className="text-[10px] uppercase tracking-wider text-text-inverse/35 truncate">
                {effectiveRole || 'Member'}
              </p>
            </div>
            <button
              type="button"
              title="Sign out"
              onClick={async () => {
                try {
                  await logout();
                } catch {
                  /* still leave */
                }
                window.location.href = '/login?reason=logged_out';
              }}
              className="p-2 rounded-xl text-text-inverse/50 hover:text-text-inverse hover:bg-bg/10 transition-colors duration-200"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      ) : null}
    </aside>
  );
}

export const OrganizerSidebar = React.memo(OrganizerSidebarComponent);
